"use client";

import { useEffect, useRef, useState } from "react";

import type { TermsMotionState } from "./use-terms-card-motion";

/**
 * Drives GuaranteesMock phase from TermsMotionState when an external/debug
 * phase is not supplied. Shared by the homepage card and restaurant visual.
 */
export function useGuaranteesPlaybackPhase(
  state: TermsMotionState,
  phase: number | undefined,
  reducedMotion: boolean,
  durationMs: number
): number {
  const [playbackPhase, setPlaybackPhase] = useState(() => {
    if (phase !== undefined) return phase;
    return 1;
  });
  const playbackStartRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (phase !== undefined) {
      setPlaybackPhase(phase);
      return;
    }

    if (reducedMotion || state === "complete") {
      setPlaybackPhase(1);
      return;
    }

    if (state === "idle") {
      setPlaybackPhase(0);
      return;
    }

    if (state !== "playing") return;

    setPlaybackPhase(0);
    playbackStartRef.current = performance.now();

    const tick = (timestamp: number) => {
      const start = playbackStartRef.current ?? timestamp;
      const linear = Math.min((timestamp - start) / durationMs, 1);
      setPlaybackPhase(linear);
      if (linear < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [state, phase, reducedMotion, durationMs]);

  if (phase !== undefined) return phase;
  if (reducedMotion || state === "complete") return 1;
  if (state === "idle") return 0;
  return playbackPhase;
}
